import { useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import useReveal from '../hooks/useReveal';
import { projects } from '../mock';

export default function CaseStudyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  useReveal();

  const { project, nextProject } = useMemo(() => {
    const idx = projects.findIndex((p) => p.slug === slug);
    if (idx === -1) return { project: null, nextProject: null };
    const next = projects[(idx + 1) % projects.length];
    return { project: projects[idx], nextProject: next };
  }, [slug]);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' }); }, [slug]);

  if (!project) {
    return (
      <div className="App">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 md:px-10 pt-40 pb-24 text-center">
          <p className="font-hand text-[26px]" style={{ color: 'var(--terracotta-deep)' }}>oops</p>
          <h1 className="font-display text-5xl mt-2" style={{ color: 'var(--ink)' }}>Case study not found</h1>
          <button
            onClick={() => navigate('/')}
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full"
            style={{ background: 'var(--ink)', color: '#FAF6EE' }}
          >
            <ArrowLeft size={16}/> Back home
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const sections = [
    { label: 'Overview', body: 'Add your project overview and brief here.' },
    { label: 'The Problem', body: 'Describe the core problem, user pain points, and business context.' },
    { label: 'Research & Insights', body: null, placeholder: 'Research & Insights — add your diagrams here' },
    { label: 'Design Process', body: 'Walk through your process — discovery, ideation, wireframes, prototypes.' },
    { label: 'Wireframes & Explorations', body: null, placeholder: 'Wireframes & Explorations — add your artefacts here' },
    { label: 'Final Design', body: 'Showcase your final screens and key decisions.' },
    { label: 'Final Screens', body: null, placeholder: 'Final Screens — add your designs here' },
    { label: 'Outcome', body: 'Share results, metrics, and what you learned.' },
  ];

  return (
    <div className="App">
      <Navbar />

      <article className="pt-32 md:pt-40 pb-24">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <Link
            to="/#work"
            className="inline-flex items-center gap-2 text-[13px] tracking-[0.12em] uppercase link-underline"
            style={{ color: 'var(--terracotta-deep)' }}
          >
            <ArrowLeft size={15} /> Back to Work
          </Link>

          <p className="reveal mt-10 text-[12.5px] tracking-[0.22em] uppercase" style={{ color: 'var(--sage)' }}>
            {project.company} · {project.year}
          </p>
          <h1
            className="reveal reveal-delay-1 font-display leading-[1.02] mt-3"
            style={{ fontSize: 'clamp(44px, 7.5vw, 96px)', color: 'var(--ink)' }}
          >
            {project.title}
          </h1>

          <div
            className="reveal reveal-delay-2 mt-10 flex flex-wrap gap-x-10 gap-y-4 pb-7"
            style={{ borderBottom: '1px solid var(--border)' }}
          >
            <MetaItem label="Role" value={project.role} />
            <MetaItem label="Platform" value={project.platform} />
            <MetaItem label="Focus" value={project.focus} />
          </div>
        </div>

        {/* Cover */}
        <div className="max-w-6xl mx-auto px-6 md:px-10 mt-12">
          <div
            className="reveal rounded-[22px] overflow-hidden shadow-warm-lg relative"
            style={{ aspectRatio: '16/7', background: project.gradient }}
          >
            {project.cover ? (
              <img
                src={project.cover}
                alt={project.title}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <>
                <div className="absolute inset-0 opacity-50" style={{
                  backgroundImage:
                    'radial-gradient(circle at 25% 20%, rgba(255,255,255,0.28), transparent 45%), radial-gradient(circle at 78% 82%, rgba(0,0,0,0.25), transparent 55%)',
                }} />
                <span className="absolute bottom-6 left-6 font-hand text-[22px]" style={{ color: 'rgba(250,246,238,0.85)' }}>
                  add cover image —
                </span>
              </>
            )}
          </div>
        </div>

        {/* Sections */}
        <div className="max-w-3xl mx-auto px-6 md:px-10 mt-20">
          <div className="space-y-16">
            {sections.map((s, i) => (
              <section key={s.label} className="reveal" style={{ transitionDelay: `${i * 30}ms` }}>
                <p className="text-[11.5px] tracking-[0.25em] uppercase mb-3" style={{ color: 'var(--sage)' }}>
                  0{i + 1}
                </p>
                <h2 className="font-display text-[32px] md:text-[40px] leading-tight mb-4" style={{ color: 'var(--ink)' }}>
                  {s.label}
                </h2>
                {s.body && (
                  <p className="text-[16.5px] leading-[1.75]" style={{ color: 'var(--ink-soft)' }}>
                    {s.body}
                  </p>
                )}
                {s.placeholder && (
                  <div
                    className="mt-2 rounded-[18px] flex items-center justify-center"
                    style={{
                      aspectRatio: '16/9',
                      background: 'var(--surface)',
                      border: '1px dashed var(--border)',
                      color: 'var(--ink-soft)',
                    }}
                  >
                    <span className="font-hand text-[20px]">{s.placeholder}</span>
                  </div>
                )}
              </section>
            ))}
          </div>

          {/* Next / Back */}
          <div className="mt-20 pt-10 flex flex-wrap items-center justify-between gap-6" style={{ borderTop: '1px solid var(--border)' }}>
            <Link
              to="/#work"
              className="inline-flex items-center gap-2 text-[13px] tracking-[0.12em] uppercase link-underline"
              style={{ color: 'var(--ink)' }}
            >
              <ArrowLeft size={15} /> Back to all work
            </Link>
            {nextProject && (
              <Link
                to={`/case/${nextProject.slug}`}
                className="group inline-flex items-center gap-3 rounded-2xl px-5 py-4 transition-all duration-300"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
              >
                <span className="text-right">
                  <span className="block text-[11px] tracking-[0.18em] uppercase" style={{ color: 'var(--sage)' }}>Next case study</span>
                  <span className="block font-display text-[18px]" style={{ color: 'var(--ink)' }}>{nextProject.title}</span>
                </span>
                <ArrowRight size={18} style={{ color: 'var(--terracotta)' }} className="group-hover:translate-x-1 transition-transform"/>
              </Link>
            )}
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}

function MetaItem({ label, value }) {
  return (
    <div>
      <p className="text-[11px] tracking-[0.22em] uppercase mb-1.5" style={{ color: 'var(--sage)' }}>{label}</p>
      <p className="text-[15px]" style={{ color: 'var(--ink)' }}>{value}</p>
    </div>
  );
}
